import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { AuthController } from '../../user-area/controllers/auth-controller';
import { SubscriptionController } from '../../user-area/controllers/subscription-controller';
import { store as authStore, selectors } from '../../user-area/redux/store';
import { showLoginModal } from '../../user-area/utils/loginModal';
import tpcQuestionApi from '../redux/api/questionApiSlice';
import { appStateSlice } from '../redux/appStateSlice';
import { AppDispatch, store } from '../redux/store';
import { ReduxController } from '../redux/utils/ReduxController';
import { Question } from '../types';
import { TPCRouter } from '../utils/router';
import { trackClickEvent } from '../../events/clicks-tracker';
import { styleMap } from 'lit/directives/style-map.js';
import { JOTFORM_IDS, setMustShowFormId } from '../../utils/showJotForm';

@customElement('tpc-corrector-question-create')
export class TPCQuestionCreate extends LitElement {
    createRenderRoot() {
        return this;
    }

    dispatch = store.dispatch as AppDispatch;
    rxController = new ReduxController(this);
    get store() {
        return this.rxController.state;
    }

    authController = new AuthController(this);
    subscriptionController = new SubscriptionController(this);

    get user() {
        return this.authController.user;
    }

    get isPremiumUser() {
        return this.user?.isPremium || this.subscriptionController.isPremiumActive;
    }

    @state()
    inputFocused = false;
    @state()
    inputText = '';
    @state()
    uploadedImage: string | null = null;

    @state()
    temporaryImage: string | null = null;

    @state()
    showUsageTooltip = false;

    get inputIsEmpty() {
        return this.inputText === '' && !this.uploadedImage;
    }

    @property({ type: Boolean })
    isLandingPage = false;

    @property({ type: Boolean })
    isWidget = false;

    @state()
    isLoading = false;

    @state()
    error: boolean = false;

    @state()
    showLimitReachedError: boolean = false;

    @state()
    errorUploadingImage: boolean = false;

    @state()
    warning: string | null = null;

    @state()
    textareaFocused = false;

    maxInputLength = 900;
    unsubscribeAuthStateChanged: () => void = () => {};

    suggestions = [
        {
            title: 'Matemática',
            question: '(3⁶ · 3⁻²) ÷ 3⁴ é igual a quê?',
        },
        {
            title: 'Português',
            question: 'Qual a diferença entre fato e opinião?',
        },
        {
            title: 'Química',
            question: 'O que é termoquímica?',
        },
        {
            title: 'História',
            question: 'Quantos presidentes o Brasil já teve?',
        },
    ];

    connectedCallback(): void {
        super.connectedCallback();
        this.unsubscribeAuthStateChanged = this.authController.onAuthStateChanged(
            ({ isLogged }) => {
                if (isLogged) {
                    this.fetchUsage();
                } else {
                    this.dispatch(appStateSlice.actions.setUsage(0));
                }
            }
        );
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.unsubscribeAuthStateChanged();
    }

    fetchUsage() {
        if (!this.user || this.isPremiumUser) return;
        this.dispatch(
            tpcQuestionApi.endpoints.getUsage.initiate({
                isTutor: this.store.appState.isTutor,
            })
        ).then((response) => {
            if (response.isSuccess) {
                this.dispatch(appStateSlice.actions.setUsage(response.data.data.usage));
            }
        });
    }

    submitSuggestion(suggestion: string) {
        this.inputText = suggestion;
        this.askQuestion();
    }

    async askQuestion() {
        this.showUsageTooltip = false;
        this.warning = null;
        if (!this.inputText && !this.uploadedImage) {
            this.warning = 'Insira sua questão e aperte <b>"Resolver"</b>';
            return;
        }
        if (this.isLoading || (!this.inputText && !this.uploadedImage)) {
            return;
        }
        if (!this.authController.isLogged) {
            try {
                localStorage.setItem(
                    'tpc-question-create-data',
                    JSON.stringify({
                        url: window.location.href,
                        inputText: this.inputText,
                        uploadedImage: this.uploadedImage,
                    })
                );
                showLoginModal(undefined, () => {
                    const storeState = authStore.getState();
                    const { token, uuid: user_id } = selectors.auth.credentials(storeState.auth);
                    const isPremiumUser = selectors.subscription.isActive(storeState.subscription);
                    if (token && user_id) {
                        if (!this.isWidget || !isPremiumUser) {
                            this.askQuestion();
                        } else {
                            this.isLoading = true;
                        }
                    }
                });
            } catch (error) {
                window.location.href = `/login/`;
            }
            return;
        }
        this.isLoading = true;
        this.error = false;

        const response = await this.dispatch(
            tpcQuestionApi.endpoints.askQuestion.initiate({
                question: this.inputText.slice(0, this.maxInputLength),
                image: this.uploadedImage,
                isTutor: this.store.appState.isTutor,
            })
        );
        localStorage.removeItem('tpc-question-create-data');
        if (response.isSuccess) {
            this.dispatch(
                tpcQuestionApi.util.updateQueryData(
                    'getPastCorrections',
                    { cursor: null, isTutor: this.store.appState.isTutor },
                    (draft) => {
                        draft.data.questions = [response.data.data.answer, ...draft.data.questions];
                    }
                )
            );
            if (!this.isLandingPage) {
                this.isLoading = false;
            }
            this.dispatch(appStateSlice.actions.setUsage(response.data.data.usage));
            if (response.data.data?.answer) {
                const showForm = response.data.data.showForm || false;
                if (showForm) {
                    setMustShowFormId(JOTFORM_IDS.AJUDANTE_TPC);
                }
                if (this.isLandingPage) {
                    window.location.href =
                        '/ajudante-ia-dever-casa/questao/' + response.data.data.answer.uuid + '/';
                } else if (this.isWidget) {
                    this.dispatch(
                        appStateSlice.actions.setSelectedQuestionUuid(
                            response.data.data.answer.uuid
                        )
                    );
                } else {
                    TPCRouter.getInstance().gotToQuestionViewPage(response.data.data.answer.uuid);
                }
            }
        } else {
            const error = response.error as FetchBaseQueryError;
            if (
                error?.data &&
                typeof error.data === 'object' &&
                'data' in error.data &&
                (error.data as any).data?.error_code === 'LIMIT_REACHED'
            ) {
                this.showLimitReachedError = true;
            } else {
                this.error = true;
                console.error('Error submitting question', response.error);
            }
            this.isLoading = false;
        }
    }

    firstUpdated() {
        this.fetchUsage();
        this.dispatch(appStateSlice.actions.setSelectedQuestionUuid(undefined));
        if (this.store.appState.usage >= 5) {
            this.showUsageTooltip = true;
        }
        window.addEventListener('triedToGoToNewExercise', () => {
            this.warning = 'Insira sua questão e aperte <b>"Resolver"</b>';
        });
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', () => {
                this.requestUpdate();
            });
        } else {
            window.addEventListener('resize', () => {
                this.requestUpdate();
            });
        }

        const data = localStorage.getItem('tpc-question-create-data');
        if (data) {
            const { url, inputText, uploadedImage } = JSON.parse(data);
            if (url === window.location.href) {
                this.inputText = inputText;
                this.uploadedImage = uploadedImage;

                if (this.user && (this.store.appState.usage < 5 || this.isPremiumUser)) {
                    this.askQuestion();
                }
            }

            localStorage.removeItem('tpc-question-create-data');
        }
    }
    clearInput() {
        this.showUsageTooltip = false;
        this.errorUploadingImage = false;
        this.inputText = '';
        this.uploadedImage = null;
        this.temporaryImage = null;
        this.warning = null;
        this.clearUploadedImage();
    }

    focusInput() {
        this.showUsageTooltip = false;
        const container = document.querySelector(
            '#tpc-corrector-question-create-textarea'
        ) as HTMLElement;
        this.warning = null;
        if (!container) return;
        container.focus();
    }

    blurInput() {
        const container = document.querySelector(
            '#tpc-corrector-question-create-textarea'
        ) as HTMLElement;
        if (!container) return;
        container.blur();
        this.textareaFocused = false;
    }

    copyFromClipboard() {
        const clipboard = navigator.clipboard;
        if (!clipboard) return;
        clipboard.readText().then((text) => {
            this.inputText = text;
            this.requestUpdate();
        });
    }
    insertExampleQuestion() {
        this.inputText = 'Qual é a capital do Brasil?';
        this.requestUpdate();
    }

    openImageInput() {
        this.clearInput();
        this.errorUploadingImage = false;
        const imageInput = document.querySelector(
            '#tpc-corrector-question-create-image-input'
        ) as HTMLInputElement;
        imageInput.click();
    }

    handleFileChange(event) {
        this.showUsageTooltip = false;
        this.warning = null;
        this.errorUploadingImage = false;
        this.blurInput();
        try {
            const file = event.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (!e.target?.result) return;
                    const blob = new Blob([e.target.result as ArrayBuffer]);
                    const url = URL.createObjectURL(blob);
                    this.resizeImage(url);
                };
                reader.readAsArrayBuffer(file);
            }
        } catch (error) {
            this.errorUploadingImage = true;
        }
    }

    resizeImage(dataUrl: string): void {
        try {
            const img: HTMLImageElement = new Image();
            img.onload = (): void => {
                const canvas: HTMLCanvasElement = document.createElement('canvas');
                let width: number = img.width;
                let height: number = img.height;
                const maxSize: number = 512;

                if (width > maxSize || height > maxSize) {
                    if (width > height) {
                        height *= maxSize / width;
                        width = maxSize;
                    } else {
                        width *= maxSize / height;
                        height = maxSize;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, width, height);
                this.temporaryImage = canvas.toDataURL('image/jpeg', 1);
            };
            img.onerror = (err) => {
                console.error('Image load error:', err);
                this.errorUploadingImage = true;
            };
            img.src = dataUrl;
        } catch (error) {
            this.errorUploadingImage = true;
        }
    }

    clearUploadedImage() {
        this.showUsageTooltip = false;
        this.uploadedImage = null;
        this.temporaryImage = null;
        const imageInput = document.querySelector(
            '#tpc-corrector-question-create-image-input'
        ) as HTMLInputElement;
        if (imageInput) {
            imageInput.value = '';
        }
    }

    renderUploadedImage() {
        if (!this.uploadedImage) return nothing;
        return html`<div class="tpc-corrector-question-create-uploaded-image">
            <div class="tpc-corrector-question-create-uploaded-image-container">
                <img src=${this.uploadedImage} />
            </div>
            ${this.isLandingPage || this.isWidget ? this.renderActions() : nothing}
        </div>`;
    }

    renderSuggestions() {
        if(this.isLandingPage || this.isWidget) return nothing;
        return html` <div class="tpc-corrector-question-create-textarea-suggestions">
            <div class="tpc-corrector-question-create-textarea-suggestions-title">
                Não sabe por onde começar?<br />Veja alguns exemplos do que você pode perguntar:
            </div>
            <div class="tpc-corrector-question-create-textarea-suggestions-items">
                ${this.suggestions.map(
                    (suggestion) => html`
                        <div
                            class="tpc-corrector-question-create-textarea-suggestions-item"
                            @click=${() => this.submitSuggestion(suggestion.question)}
                        >
                            <div
                                class="tpc-corrector-question-create-textarea-suggestions-item-title"
                            >
                                ${suggestion.title}
                            </div>
                            <div
                                class="tpc-corrector-question-create-textarea-suggestions-item-question"
                            >
                                ${suggestion.question}
                            </div>
                        </div>
                    `
                )}
            </div>
        </div>`;
    }

    renderPlaceholder() {
        return html`<div
            class="tpc-corrector-question-create-textarea-placeholder"
            @click=${this.focusInput}
        >
            <div class="tpc-corrector-question-create-textarea-placeholder--text">
                Insira ou cole sua questão (Ctrl + V). Depois, aperte Resolver.
            </div>
            <div class="tpc-corrector-question-create-textarea-placeholder--buttons">
                <button class="ds-button ds-button--light-gray" @click=${this.copyFromClipboard}>
                    <span class="icon icon-078-copy-outline"></span>
                    Colar texto
                </button>
                <div class="tpc-corrector-question-create-textarea-placeholder--or">ou</div>
                <label
                    for="tpc-corrector-question-create-image-input"
                    class="ds-button ds-button--light-gray"
                >
                    <span class="icon icon-076-image"></span>
                    Inserir imagem
                </label>
            </div>
            ${this.renderSuggestions()}
        </div>`;
    }

    renderWarning() {
        if (!this.warning) return nothing;
        return html`<div class="tpc-corrector-question-create-textarea-warning">
            <span class="icon icon-054-alert-outline"></span>
            <div>${unsafeHTML(this.warning)}</div>
            <button
                class="tpc-corrector-question-create-textarea-warning-close"
                @click=${() => {
                    this.warning = null;
                }}
            >
                <span class="icon icon-012-close"></span>
            </button>
        </div> `;
    }

    get isMobile() {
        return window.innerWidth < 768;
    }

    get containerDistanceToTopOfPage() {
        const container = document.querySelector(
            '.tpc-corrector-question-create-container'
        ) as HTMLElement;
        if (!container) return 0;
        return container.offsetTop;
    }

    get baseMinHeight() {
        return this.isLandingPage || this.isWidget ? 200 : 520;
    }

    get visualHeight() {
        if (!window.visualViewport) return this.baseMinHeight + this.containerDistanceToTopOfPage;
        return window.visualViewport.height;
    }

    get focusedInputHeight() {
        if ((this.isLandingPage || this.isWidget) && this.uploadedImage) {
            return '420px';
        }

        const height =
            this.textareaFocused && this.isMobile
                ? this.visualHeight - this.containerDistanceToTopOfPage
                : this.baseMinHeight;

        if (height > this.baseMinHeight) {
            return `${this.baseMinHeight}px`;
        }

        return `${height}px`;
    }

    renderActions() {
        return html`<div class="tpc-corrector-question-create-actions">
            <div class="tpc-corrector-question-create-actions-left-buttons">
                ${this.uploadedImage
                    ? html`
                          <label
                              tabindex="0"
                              class="${classMap({
                                  'ds-button ds-button--primary ds-button--white tpc-corrector-question-create-actions-button':
                                      true,
                                  'tpc-corrector-question-create-actions-button--formula': true,
                                  'tpc-corrector-question-create-actions-button--formula-minimal':
                                      !this.inputIsEmpty,
                              })}"
                              for="tpc-corrector-question-create-image-input"
                          >
                              <span class="icon icon-077-image-replace"></span
                              ><span class="tpc-corrector-question-create-actions-button-text"
                                  >Substituir imagem</span
                              >
                          </label>
                          <div class="spacer"></div>
                      `
                    : nothing}
                ${this.inputIsEmpty
                    ? nothing
                    : html` <button
                          class="ds-button ds-button--primary ds-button--white tpc-corrector-question-create-actions-button"
                          @click=${this.clearInput}
                      >
                          <span class="icon icon-073-bin-outline"></span>
                          ${!this.uploadedImage ? html` Limpar texto ` : html`Remover imagem`}
                      </button>`}
            </div>
            <button
                class="ds-button ds-button--primary tpc-corrector-question-create-actions-submit"
                @click=${() => {
                    trackClickEvent('cta-assistant-widget-resolver');
                    this.askQuestion();
                }}
                .disabled=${this.isLoading}
            >
                ${this.isLoading
                    ? 'Aguarde...'
                    : html`Resolver <span class="icon icon-034-dash-arrow"></span>`}
            </button>
        </div>`;
    }

    render() {
        if (this.isLoading) {
            return html`
                <tpc-corrector-question-view
                    .question=${{
                        id: '-99',
                        title: 'Nova Questão',
                        question: this.inputText,
                        image: this.uploadedImage,
                    } as Question}
                    .isLoadingPlaceHolder=${this.isLoading}
                ></tpc-corrector-question-view>
            `;
        }
        return html`
            <div
                class="${classMap({
                    'tpc-corrector-question-create-container': true,
                    'tpc-corrector-question-create-container--focused': this.textareaFocused,
                    'tpc-corrector-question-create-container--landing-page':
                        this.isLandingPage || this.isWidget,
                })}"
                style="${styleMap({
                    minHeight: this.focusedInputHeight,
                })}"
            >
                ${!(this.isLandingPage || this.isWidget)
                    ? html`<div class="tpc-corrector-question-create-title">
                          <span class="icon icon-050-doc-write"></span>
                          Escreva sua questão<span
                              class="tpc-corrector-question-create-title-subtext"
                              >e aprenda com a resposta</span
                          >
                      </div> `
                    : nothing}
                <div
                    class="tpc-corrector-question-create-textarea-container ${this.textareaFocused
                        ? 'tpc-corrector-question-create-textarea-container--focused'
                        : ''}"
                >
                    ${!this.uploadedImage
                        ? html` <textarea
                                  class="tpc-corrector-question-create-textarea-content"
                                  id="tpc-corrector-question-create-textarea"
                                  maxlength="${this.maxInputLength}"
                                  .value=${this.inputText}
                                  @input=${(e) => {
                                      this.inputText = (e.target as HTMLTextAreaElement).value;
                                      this.warning = null;
                                  }}
                                  @focus=${() => {
                                      this.textareaFocused = true;
                                      this.showUsageTooltip = false;
                                      if (this.isMobile) {
                                          this.scrollIntoView({
                                              block: 'center',
                                          });
                                      }
                                  }}
                                  @focusout=${(e) => {
                                      if (e.relatedTarget && this.isMobile) {
                                          const preventUnFocusClasses = [
                                              'tpc-corrector-question-create-actions-submit',
                                              'tpc-corrector-question-create-actions-button',
                                          ];
                                          if (
                                              preventUnFocusClasses.some((cls) =>
                                                  e.relatedTarget.className.includes(cls)
                                              )
                                          ) {
                                              e.preventDefault();
                                              return;
                                          }
                                      }
                                      this.textareaFocused = false;
                                  }}
                                  @blur=${(e) => {
                                      if (e.relatedTarget && this.isMobile) {
                                          const preventUnFocusClasses = [
                                              'tpc-corrector-question-create-actions-submit',
                                              'tpc-corrector-question-create-actions-button',
                                          ];
                                          if (
                                              preventUnFocusClasses.some((cls) =>
                                                  e.relatedTarget.className.includes(cls)
                                              )
                                          ) {
                                              e.preventDefault();
                                              return;
                                          }
                                      }
                                      this.textareaFocused = false;
                                  }}
                              ></textarea>
                              <div class="tpc-corrector-question-create-textarea-info">
                                  <div
                                      class="tpc-corrector-question-create-textarea-info-text ${this
                                          .inputText.length >= this.maxInputLength
                                          ? 'tpc-corrector-question-create-textarea-info-text--usage--limit'
                                          : ''}"
                                  >
                                      Caracteres: ${this.inputText.length}/${this.maxInputLength}
                                  </div>
                                  ${!this.isPremiumUser || !this.user
                                      ? html`<div
                                            class="tpc-corrector-question-create-textarea-info-text
                                                tpc-corrector-question-create-textarea-info-text--usage ${this
                                                .store.appState.usage >= 5
                                                ? 'tpc-corrector-question-create-textarea-info-text--usage--limit'
                                                : ''}
                                                "
                                        >
                                            <div
                                                class="tpc-corrector-question-create-textarea-info-text--usage-text"
                                                @click=${() => {
                                                    this.showUsageTooltip = !this.textareaFocused;
                                                }}
                                            >
                                                Utilizações: ${this.store.appState.usage}/5
                                            </div>
                                            ${!this.showUsageTooltip
                                                ? nothing
                                                : html`
                                                      <div
                                                          class="tpc-corrector-question-create-textarea-info-text--usage-tooltip-triangle"
                                                      ></div>
                                                      <div
                                                          class="tpc-corrector-question-create-textarea-info-text--usage-tooltip"
                                                      >
                                                          <div
                                                              class="tpc-corrector-question-create-textarea-info-text--usage-tooltip-container"
                                                          >
                                                              <div
                                                                  class="tpc-corrector-question-create-textarea-info-text--usage-tooltip-header"
                                                              >
                                                                  <span
                                                                      class="icon icon-066-alert-outline-round"
                                                                  ></span>
                                                                  Utilizações:
                                                                  ${this.store.appState.usage}/5
                                                                  <span
                                                                      class="icon icon-027-outline-close"
                                                                      @click=${() => {
                                                                          this.showUsageTooltip =
                                                                              false;
                                                                      }}
                                                                  ></span>
                                                              </div>
                                                              <div
                                                                  class="tpc-corrector-question-create-textarea-info-text--usage-tooltip-content"
                                                              >
                                                                  Resolva questões sem limite,
                                                                  <a
                                                                      href="/tmplus/"
                                                                      @click="${() => {
                                                                          trackClickEvent(
                                                                              'cta-tmplus-assistant-usage-limit'
                                                                          );
                                                                      }}"
                                                                      >desbloqueie o acesso
                                                                      completo!</a
                                                                  >
                                                              </div>
                                                          </div>
                                                      </div>
                                                  `}
                                        </div>`
                                      : nothing}
                                  ${this.isLandingPage || this.isWidget
                                      ? html`<button
                                            class="ds-button ds-button--primary tpc-corrector-question-create-textarea-info-submit"
                                            @click=${() => {
                                                trackClickEvent('cta-assistant-widget-resolver');
                                                this.askQuestion();
                                            }}
                                            .disabled=${this.isLoading}
                                        >
                                            ${this.isLoading
                                                ? 'Aguarde...'
                                                : html`Resolver
                                                      <span
                                                          class="icon icon-034-dash-arrow"
                                                      ></span>`}
                                        </button>`
                                      : nothing}
                              </div>
                              ${this.inputIsEmpty ? this.renderPlaceholder() : nothing}`
                        : this.renderUploadedImage()}
                    ${this.renderWarning()}
                </div>
                <input
                    type="file"
                    accept="image/*"
                    id="tpc-corrector-question-create-image-input"
                    style="display: none;"
                    @change=${this.handleFileChange}
                />
                ${!(this.isLandingPage || this.isWidget) ? this.renderActions() : nothing}
            </div>
            ${this.temporaryImage
                ? html`
                      <tm-tpc-corrector-temporary-image-modal
                          .imageUrl=${this.temporaryImage}
                          @confirm=${() => {
                              this.uploadedImage = this.temporaryImage;
                              this.temporaryImage = null;
                              this.askQuestion();
                          }}
                          @cancel=${() => {
                              this.temporaryImage = null;
                              this.clearUploadedImage();
                          }}
                      ></tm-tpc-corrector-temporary-image-modal>
                  `
                : nothing}
            ${this.errorUploadingImage
                ? html`
                      <tm-tpc-corrector-error-modal
                          .isImageError=${true}
                          @tryAgain=${this.openImageInput}
                          @newQuestion=${this.clearInput}
                      ></tm-tpc-corrector-error-modal>
                  `
                : nothing}
            ${this.error
                ? html`
                      <tm-tpc-corrector-error-modal
                          @tryAgain=${this.askQuestion}
                          @newQuestion=${this.clearInput}
                      ></tm-tpc-corrector-error-modal>
                  `
                : this.showLimitReachedError
                ? html`
                      <tm-tpc-corrector-limit-error-modal
                          @close=${() => {
                              this.showLimitReachedError = false;
                          }}
                      ></tm-tpc-corrector-limit-error-modal>
                  `
                : nothing}
        `;
    }
}
